/**
 * ThemeCard - Hybrid preview card with 3D tilt effect
 *
 * Shows static thumbnail by default, expands to live iframe on hover.
 * Features 3D perspective tilt following cursor position.
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import type { LayoutTheme } from '@/types/layout-theme'

interface ThemeCardProps {
  theme: LayoutTheme
  name: string
  descriptor: string
  previewUrl?: string
}

// Theme-specific preview colors/gradients for placeholder thumbnails
const THEME_GRADIENTS: Record<LayoutTheme, string> = {
  'mac-studio': 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
  'modern-cards': 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 50%, #ffffff 100%)',
  'matrix-terminal': 'linear-gradient(135deg, #000000 0%, #0a0a0a 50%, #001100 100%)',
  'mac-os-classic': 'linear-gradient(135deg, #c0c0c0 0%, #d4d4d4 50%, #e8e8e8 100%)',
  'executive-dashboard': 'linear-gradient(135deg, #1e1e2f 0%, #2d2d44 50%, #1a1a2e 100%)',
  'terminal-explorer': 'linear-gradient(135deg, #0a0e27 0%, #0f1419 50%, #001100 100%)',
}

// Theme icons/emoji for visual hint
const THEME_ICONS: Record<LayoutTheme, string> = {
  'mac-studio': '',
  'modern-cards': '',
  'matrix-terminal': '>_',
  'mac-os-classic': '',
  'executive-dashboard': '',
  'terminal-explorer': '⌨️',
}

export function ThemeCard({ theme, name, descriptor, previewUrl }: ThemeCardProps) {
  const navigate = useNavigate()
  const cardRef = useRef<HTMLDivElement>(null)
  const [isHovering, setIsHovering] = useState(false)
  const [showIframe, setShowIframe] = useState(false)
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // 3D tilt effect
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const card = cardRef.current
    if (!card) return

    // Check reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return
    }

    const rect = card.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const centerX = rect.width / 2
    const centerY = rect.height / 2

    const rotateX = ((y - centerY) / centerY) * -5
    const rotateY = ((x - centerX) / centerX) * 5

    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`
  }, [])

  const handleMouseEnter = useCallback(() => {
    setIsHovering(true)

    // Show iframe after 500ms hover
    hoverTimeoutRef.current = setTimeout(() => {
      setShowIframe(true)
    }, 500)
  }, [])

  const handleMouseLeave = useCallback(() => {
    const card = cardRef.current
    if (card) {
      card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)'
    }

    setIsHovering(false)
    setShowIframe(false)

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
  }, [])

  const handleClick = useCallback(() => {
    navigate(`/explore?theme=${theme}`)
  }, [navigate, theme])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        handleClick()
      }
    },
    [handleClick]
  )

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])

  // Add cache-busting parameter to ensure fresh iframe loads with correct theme
  const iframeUrl = previewUrl || `/explore?theme=${theme}&preview=true&_t=${Date.now()}`

  return (
    <div
      ref={cardRef}
      className="theme-card"
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-label={`Select ${name} theme: ${descriptor}`}
      style={{
        background: 'rgba(20, 20, 20, 0.4)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        overflow: 'hidden',
        cursor: 'pointer',
        transformStyle: 'preserve-3d',
        transition: 'transform 0.1s ease-out, box-shadow 0.3s ease',
        backdropFilter: 'blur(5px)',
        boxShadow: isHovering
          ? '0 20px 40px rgba(0, 240, 255, 0.1), 0 0 0 1px rgba(0, 240, 255, 0.3)'
          : '0 4px 20px rgba(0, 0, 0, 0.3)',
      }}
    >
      {/* Accent bar */}
      <div
        className="theme-card__accent"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '4px',
          height: '100%',
          background: '#00f0ff',
          transform: isHovering ? 'scaleY(1)' : 'scaleY(0)',
          transformOrigin: 'bottom',
          transition: 'transform 0.4s ease',
          zIndex: 2,
        }}
      />

      {/* Preview area */}
      <div
        className="theme-card__preview"
        style={{
          position: 'relative',
          aspectRatio: '16 / 10',
          background: THEME_GRADIENTS[theme],
          overflow: 'hidden',
        }}
      >
        {/* Theme icon overlay */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '3rem',
            opacity: showIframe ? 0 : 0.5,
            transition: 'opacity 0.3s ease',
            zIndex: 1,
            color: theme === 'modern-cards' || theme === 'mac-os-classic' ? '#333' : '#fff',
          }}
        >
          {THEME_ICONS[theme]}
        </div>

        {/* Live iframe (shown after hover delay) */}
        {showIframe && (
          <iframe
            src={iframeUrl}
            title={`${name} preview`}
            style={{
              position: 'absolute',
              inset: 0,
              width: '400%',
              height: '400%',
              transform: 'scale(0.25)',
              transformOrigin: 'top left',
              border: 'none',
              pointerEvents: 'none',
              opacity: 1,
            }}
            loading="lazy"
          />
        )}
      </div>

      {/* Card content */}
      <div
        className="theme-card__content"
        style={{
          padding: '1.25rem',
          transform: 'translateZ(20px)',
        }}
      >
        <h3
          style={{
            fontSize: '1.2rem',
            fontWeight: 700,
            color: '#e0e0e0',
            margin: 0,
            marginBottom: '0.25rem',
          }}
        >
          {name}
        </h3>
        <p
          style={{
            fontSize: '0.875rem',
            color: '#888',
            margin: 0,
            fontFamily: "'Courier New', monospace",
          }}
        >
          {descriptor}
        </p>
      </div>
    </div>
  )
}

export default ThemeCard
